import { useCallback, useRef, useState } from "react";

const getStopwatchNow = () => window.performance?.now?.() || Date.now();

const getProfiler = () => window.__PGWebSdk?._profiler;

const getProfilerSnapshot = ({
  totalElapsedMilliseconds,
  startedAt,
  finishedAt
}) => {
  const profiler = getProfiler();
  if (!profiler || typeof profiler.getEvents !== "function") {
    return null;
  }

  const events = profiler.getEvents();
  if (!Array.isArray(events) || events.length === 0) {
    return null;
  }

  const report =
    typeof profiler.getReport === "function"
      ? profiler.getReport()
      : [...events].sort((a, b) => (b.elapsed || 0) - (a.elapsed || 0));

  return {
    totalElapsedMilliseconds,
    startedAt: startedAt?.toISOString?.(),
    finishedAt: finishedAt?.toISOString?.(),
    events,
    report
  };
};

export const formatStopwatchElapsed = (elapsedMilliseconds = 0) => {
  const safeElapsedMilliseconds = Math.max(0, Math.round(elapsedMilliseconds));
  const minutes = Math.floor(safeElapsedMilliseconds / 60000);
  const seconds = Math.floor((safeElapsedMilliseconds % 60000) / 1000);
  const milliseconds = safeElapsedMilliseconds % 1000;

  return `${minutes}:${String(seconds).padStart(2, "0")}.${String(
    milliseconds
  ).padStart(3, "0")}`;
};

export const usePaymentStopwatch = () => {
  const stopwatchStartRef = useRef(null);
  const stopwatchStartedAtRef = useRef(null);
  const elapsedBeforePauseRef = useRef(0);
  const isPausedRef = useRef(false);
  const resetProfilerOnResumeRef = useRef(false);
  const [paymentResult, setPaymentResult] = useState(null);

  const start = useCallback(() => {
    if (isPausedRef.current) {
      const profiler = getProfiler();

      if (resetProfilerOnResumeRef.current) {
        if (typeof profiler?.clear === "function") {
          profiler.clear();
        }

        elapsedBeforePauseRef.current = 0;
        stopwatchStartedAtRef.current = new Date();
        resetProfilerOnResumeRef.current = false;
      } else if (typeof profiler?.resume === "function") {
        profiler.resume();
      }

      stopwatchStartRef.current = getStopwatchNow();
      isPausedRef.current = false;
      setPaymentResult(null);
      return;
    }

    if (stopwatchStartRef.current == null) {
      const profiler = getProfiler();
      if (typeof profiler?.clear === "function") {
        profiler.clear();
      }

      elapsedBeforePauseRef.current = 0;
      resetProfilerOnResumeRef.current = false;
      stopwatchStartRef.current = getStopwatchNow();
      stopwatchStartedAtRef.current = new Date();
    }

    setPaymentResult(null);
  }, []);

  const pause = useCallback(() => {
    if (isPausedRef.current || stopwatchStartRef.current == null) {
      return;
    }

    elapsedBeforePauseRef.current +=
      getStopwatchNow() - stopwatchStartRef.current;
    stopwatchStartRef.current = null;
    isPausedRef.current = true;

    const profiler = getProfiler();
    if (typeof profiler?.pause === "function") {
      profiler.pause();
    } else if (profiler) {
      resetProfilerOnResumeRef.current = true;
    }
  }, []);

  const resume = useCallback(() => {
    if (!isPausedRef.current) {
      return;
    }

    start();
  }, [start]);

  const finish = useCallback((result) => {
    const finishedAt = getStopwatchNow();
    const finishedAtDate = new Date();
    const startedAt = stopwatchStartRef.current ?? finishedAt;
    const startedAtDate = stopwatchStartedAtRef.current ?? finishedAtDate;
    const elapsedMilliseconds =
      elapsedBeforePauseRef.current +
      (isPausedRef.current ? 0 : finishedAt - startedAt);

    stopwatchStartRef.current = null;
    stopwatchStartedAtRef.current = null;
    elapsedBeforePauseRef.current = 0;
    isPausedRef.current = false;
    resetProfilerOnResumeRef.current = false;

    setPaymentResult({
      ...result,
      elapsedMilliseconds,
      profiling: getProfilerSnapshot({
        totalElapsedMilliseconds: elapsedMilliseconds,
        startedAt: startedAtDate,
        finishedAt: finishedAtDate
      })
    });
  }, []);

  const reset = useCallback(() => {
    stopwatchStartRef.current = null;
    stopwatchStartedAtRef.current = null;
    elapsedBeforePauseRef.current = 0;
    isPausedRef.current = false;
    resetProfilerOnResumeRef.current = false;

    const profiler = getProfiler();
    if (typeof profiler?.clear === "function") {
      profiler.clear();
    }
  }, []);

  const close = useCallback(() => {
    setPaymentResult(null);
  }, []);

  return {
    start,
    pause,
    resume,
    finish,
    reset,
    close,
    paymentResult
  };
};
